import React from 'react';

// styles
import { Spacing } from '_styles';
import { PRM_BLUE, GRAY_LIGHT, GRAY_DARK, TEXT_COLOR } from '_styles/colors';

// bootstrap
import { Card } from 'react-bootstrap';

/**
 * Flight Event Card Component
 * Displays a single flight event in a style similar to flight-feed-updates
 */
const FlightEventCard = ({ event, index }) => {

    // Extract flight info from flightId (e.g., "BFS_U23077_2026-01-20" or "BFS_D801_2026-01-20")
    const flightParts = event?.flightId?.split('_') || [];
    const flightNumber = flightParts[1] || event?.flightNumber || 'Unknown';

    // Format ISO string to time and date
    const formatIsoString = (isoString) => {
        if (!isoString) return { time: 'N/A', date: 'N/A' };
        try {
            const date = new Date(isoString);
            const pad = (num) => String(num).padStart(2, '0');
            const hours = pad(date.getHours());
            const minutes = pad(date.getMinutes());
            const seconds = pad(date.getSeconds());
            const year = date.getFullYear();
            const month = pad(date.getMonth() + 1);
            const day = pad(date.getDate());
            return {
                time: `${hours}:${minutes}:${seconds}`,
                date: `${year}-${month}-${day}`
            };
        } catch {
            return { time: 'N/A', date: 'N/A' };
        }
    };

    // Format field name to be more readable
    const formatFieldName = (fieldName) => {
        if (!fieldName) return 'Unknown';
        const parts = fieldName.split('.');
        const lastPart = parts[parts.length - 1];
        return lastPart
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    // Get change type label (e.g., "TIME CHANGED", "GATE CHANGED")
    const getChangeTypeLabel = (fieldName) => {
        if (!fieldName) return 'FIELD CHANGED';
        const lowerField = fieldName.toLowerCase();
        
        if (lowerField.includes('time')) return 'TIME CHANGED';
        if (lowerField.includes('gate')) return 'GATE CHANGED';
        if (lowerField.includes('stand')) return 'STAND CHANGED';
        if (lowerField.includes('terminal')) return 'TERMINAL CHANGED';
        if (lowerField.includes('status')) return 'STATUS CHANGED';
        if (lowerField.includes('delay')) return 'DELAY CHANGED';
        
        // Default: format the field name
        const parts = fieldName.split('.');
        const lastPart = parts[parts.length - 1];
        return lastPart
            .replace(/([A-Z])/g, ' $1')
            .toUpperCase()
            .trim() + ' CHANGED';
    };

    // Format value (handles timestamps and other values)
    const formatValue = (value) => {
        if (!value) return 'N/A';
        
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
            const formatted = formatIsoString(value);
            return `${formatted.time} (${formatted.date})`;
        }
        
        return String(value);
    };

    // Get changed fields
    const changedFields = event?.changedFields || [];
    const beforeValues = event?.before || {};
    const afterValues = event?.after || {};

    // Get header color based on event type
    const getHeaderColor = () => {
        switch (event?.type) {
            case 'FLIGHT_CHANGE':
                return PRM_BLUE;
            case 'GATE_CHANGE':
                return '#ffc107';
            case 'DELAY':
                return '#dc3545';
            case 'CANCELLATION':
                return '#dc3545';
            default:
                return '#6c757d';
        }
    };

    //#region styling
    const cardStyle = {
        marginBottom: Spacing.SCALE_16,
        border: `1px solid ${GRAY_LIGHT}`,
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        backgroundColor: 'white'
    };

    const headerStyle = {
        backgroundColor: getHeaderColor(),
        color: 'white',
        padding: Spacing.SCALE_12,
        borderRadius: '8px 8px 0 0',
        margin: 0,
        display: 'flex',
        justifyContent: 'space-between',
    };

    const contentStyle = {
        padding: Spacing.SCALE_16
    };

    const labelStyle = {
        fontSize: '12px',
        fontWeight: '600',
        color: GRAY_DARK,
        marginBottom: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    };

    const valueStyle = {
        fontSize: '14px',
        color: TEXT_COLOR,
        margin: 0,
        fontWeight: '500'
    };
    //#endregion

    return (
        <Card style={cardStyle}>
            <Card.Header style={headerStyle}>

                {/* Flight number and event type */}
                <div>
                    <p style={{ margin: 0, color: 'white', fontSize: '16px', fontWeight: '600' }}>
                        {flightNumber}
                    </p>
                    <p style={{ margin: 0, color: 'white', fontSize: '12px', opacity: 0.9 }}>
                        {event?.type?.replace('_', ' ') || 'EVENT'}
                    </p>
                </div>

                {/* Timestamp */}
                <div style={{ textAlign: 'right' }}>
                    {event?.eventTime ? (
                        <>
                            <p style={{ margin: 0, color: 'white', fontSize: '16px', fontWeight: '600' }}>
                                {formatIsoString(event.eventTime).time}
                            </p>
                            <p style={{ margin: 0, color: 'white', fontSize: '12px', opacity: 0.9 }}>
                                {formatIsoString(event.eventTime).date}
                            </p>
                        </>
                    ) : (
                        <p style={{ margin: 0, color: 'white', fontSize: '16px' }}>N/A</p>
                    )}
                </div>
            </Card.Header>

            <Card.Body style={contentStyle}>
                {changedFields.length > 0 ? (
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: Spacing.SCALE_12
                    }}>
                        {changedFields.map((field, idx) => (
                            <div key={idx}>
                                {/* Change type label */}
                                <div style={{
                                    backgroundColor: '#ffc107',
                                    color: '#856404',
                                    padding: '4px 12px',
                                    borderRadius: '4px 4px 0 0',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    letterSpacing: '0.5px',
                                    display: 'inline-block'
                                }}>
                                    {getChangeTypeLabel(field)}
                                </div>

                                {/* Field change details */}
                                <div 
                                    style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'flex-start',
                                        gap: Spacing.SCALE_16,
                                        padding: Spacing.SCALE_12,
                                        backgroundColor: '#fff3cd',
                                        border: '2px solid #ffc107',
                                        borderRadius: '0 6px 6px 6px'
                                    }}
                                >
                                    {/* Before value */}
                                    <div style={{ flex: 1, textAlign: 'center' }}>
                                        <div style={labelStyle}>Before</div>
                                        <div style={{ 
                                            ...valueStyle, 
                                            color: '#dc3545',
                                            textDecoration: 'line-through'
                                        }}>
                                            {formatValue(beforeValues[field])}
                                        </div>
                                    </div>

                                    {/* After value */}
                                    <div style={{ flex: 1, textAlign: 'center' }}>
                                        <div style={labelStyle}>After</div>
                                        <div style={{ 
                                            ...valueStyle, 
                                            color: '#28a745',
                                            fontWeight: '600'
                                        }}>
                                            {formatValue(afterValues[field])}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', color: GRAY_DARK }}>
                        No field changes recorded
                    </div>
                )}
            </Card.Body>

            {/* Footer with meta info */}
            <Card.Footer style={{ backgroundColor: '#f8f9fa', borderTop: `1px solid ${GRAY_LIGHT}`, borderRadius: '0 0 8px 8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <small style={{ color: GRAY_DARK }}>
                        IATA: {event?.iata || 'N/A'}
                    </small>
                    <small style={{ color: GRAY_DARK }}>
                        Feed: {event?.meta?.feed || 'N/A'}
                    </small>
                </div>
            </Card.Footer>

        </Card>
    );
};

export default FlightEventCard;